/**
 * Where every piece of a formula goes.
 *
 * Typesetting is *arithmetic on boxes*. A fraction is not a drawing trick: the
 * bar is as wide as the wider of its two children, the children are centred on
 * it, and the whole thing is as tall as the numerator above the bar and the
 * denominator below. A superscript is a smaller box, raised clear of its base. A
 * radical is a stroke whose height is the height of what it covers. Get those
 * numbers right and `\frac{x+1}{2x-3}` comes out of an SVG as a real fraction —
 * and, because the numbers depend on the *height* of a child rather than on its
 * text, a fraction inside a fraction comes out correctly too, which is the thing
 * a flat string of characters can never do.
 *
 * So this module does the arithmetic and nothing else. It takes the tree from
 * `parse.ts` and a way to measure text, and returns a *display list*: runs of
 * glyphs, rectangles and strokes, each with its position already decided — y
 * growing downwards, x from the left edge, and the origin on the baseline, so
 * `-height` is the top of the formula and `+depth` its bottom.
 *
 * Two consequences of doing it this way, both deliberate:
 *
 *  - **Nothing here draws.** The display list can be handed to any renderer, and
 *    it can be *read by a test*: "the numerator's baseline is above the bar" is a
 *    number to assert, not a picture to squint at.
 *  - **Nothing here measures the platform.** Widths arrive through `measure`, so
 *    the same engine lays out identically whether the number came from a real
 *    text engine (see `measure.tsx`) or from the estimate used on the frame
 *    before the real numbers land.
 */

import {LIMIT_OPERATOR_GLYPHS, LIMIT_OPERATOR_NAMES} from './commands'
import {type TexNode} from './parse'

/**
 * TeX's four styles, which is where every size in a formula comes from.
 *
 * The point of naming them: in *display* style a fraction's children are set in
 * text style — the same size — while a fraction one level deeper is set at script
 * size, 72% of it. That single rule is what makes nesting read as nesting.
 */
type Style = 'display' | 'text' | 'script' | 'scriptscript'

const SIZE_OF: Record<Style, number> = {
  display: 1,
  text: 1,
  script: 0.72,
  scriptscript: 0.55,
}

/** The style a fraction's children are set in. */
const inFraction: Record<Style, Style> = {
  display: 'text',
  text: 'script',
  script: 'scriptscript',
  scriptscript: 'scriptscript',
}

/** The style a script's operand is set in. */
const inScript: Record<Style, Style> = {
  display: 'script',
  text: 'script',
  script: 'scriptscript',
  scriptscript: 'scriptscript',
}

/**
 * The vertical model, in ems.
 *
 * A glyph is treated as one box: `ASCENT` above the baseline, `DESCENT` below.
 * The real numbers per glyph are in the font file, and reading them would be
 * more faithful and less useful — the formula is set in whatever face the theme
 * gives the page, and its metrics are the platform's business, not ours. What
 * matters for typesetting is that the *same* box is used everywhere, so a
 * fraction bar lands in the same place whether the reader's device measures a
 * numerator as 40px or 41px wide.
 */
const ASCENT = 0.74
const DESCENT = 0.22

/** Thickness of every rule the formula draws, as a multiple of the type size. */
const RULE = 0.05
/** Air between a fraction's children and its bar, measured to their ink. */
const FRACTION_GAP = 0.22
/**
 * The x-height, in ems: how high a run rises when nothing in it is tall.
 *
 * Only ever used as the *smaller* of two answers — a run is assumed to reach the
 * ascender line unless every character in it is known to be short — because the
 * two mistakes are not equal: a denominator set a hair low leaves a little more
 * air under the bar, while one set a hair high has the bar through it.
 */
const X_HEIGHT = 0.5
/** Characters that hang below the baseline, and so set a run's descent. */
const DESCENDERS = /[gjpqy,;()\[\]{}|∫βγζημξρφχψ]/
/** A run made only of these rises no higher than the x-height. */
const LOW_ONLY = /^[acemnorsuvwxz+\-=−<>≤≥≠≈±∓×÷·∈∉⊂⊆∪∩∧∨∗,.]*$/
/** Air either side of a fraction's widest child. */
const FRACTION_PAD = 0.1
/** How far a superscript's baseline rises, at minimum. */
const SUP_SHIFT = 0.45
/** How far a subscript's baseline drops, at minimum. */
const SUB_SHIFT = 0.22
/** Air between a radical's bar and what it covers. */
const RADICAL_GAP = 0.1
/** The width of a radical's stroke and the room the stroke takes. */
const RADICAL_RULE = 0.07
const RADICAL_WIDTH = 0.52
/** How much larger a big operator is set than the text around it. */
const LIMIT_OPERATOR_SCALE = 1.35
/** Air between a big operator and its limits. */
const LIMITS_GAP = 0.16
/** Gap between the lines of a formula written with `\\`. */
const LINE_GAP = 0.6

/** A run of glyphs, with the baseline it sits on. */
export type Run = {
  text: string
  italic: boolean
  size: number
  x: number
  y: number
}

/** A filled rectangle: a fraction bar, a radical's overbar, a vector's rule. */
export type Rect = {x: number; y: number; width: number; height: number}

/** An open polyline: the radical's stroke, a vector's arrowhead. */
export type Stroke = {points: Array<[number, number]>; width: number}

export type DisplayList = {
  runs: Run[]
  rects: Rect[]
  strokes: Stroke[]
  /** The formula's width. */
  width: number
  /** How far it reaches above the baseline (a positive number). */
  height: number
  /** And below it. */
  depth: number
}

/**
 * How wide a piece of text is, in px, when set at `size` in the face the formula
 * is printed in.
 *
 * Passed in rather than looked up, so that the engine has no opinion about where
 * the number came from — and so a test can lay a formula out with numbers it
 * chose, which is the only way to assert on the arithmetic instead of on a
 * browser.
 */
export type Measure = (
  text: string,
  spec: {italic: boolean; size: number},
) => number

export type LayoutOptions = {
  /** The size of the formula at display scale, in px. */
  size: number
  measure: Measure
}

/** A laid-out box, in progress: extents plus everything drawn inside it. */
type Box = {
  width: number
  height: number
  depth: number
  runs: Run[]
  rects: Rect[]
  strokes: Stroke[]
}

const emptyBox = (width = 0): Box => ({
  width,
  height: 0,
  depth: 0,
  runs: [],
  rects: [],
  strokes: [],
})

/**
 * How far a laid-out box's ink reaches above its own baseline.
 *
 * The uniform box model gives every glyph the same ascent and descent. That is
 * the right envelope for *stacking* — lines and scripts must not collide, and
 * being a little generous costs nothing — and the wrong answer for a fraction,
 * where the only question is whether the ink touches the rule. Measured from the
 * boxes, `\frac{x+1}{2x-3}` came out with its numerator floating nearly half an
 * em clear of the bar while the denominator sat tucked under it, because the
 * numerator's descent was reserved for a tail nothing in it had.
 *
 * So a fraction's clearance is taken to the ink, read off what the box actually
 * draws: a run rises to the ascender line only if something in it is tall, and
 * hangs below the baseline only if something in it actually descends. Everything
 * the guide-lines here are *generous* in the safe direction.
 */
function inkAbove(box: Box): number {
  let up = 0
  for (const run of box.runs) {
    const rise = LOW_ONLY.test(run.text) ? X_HEIGHT : ASCENT
    up = Math.max(up, -run.y + rise * run.size)
  }
  for (const rect of box.rects) up = Math.max(up, -rect.y)
  for (const stroke of box.strokes) {
    for (const [, y] of stroke.points) up = Math.max(up, -y + stroke.width / 2)
  }
  return Math.max(0, up)
}

/** How far a laid-out box's ink reaches below its own baseline. */
function inkBelow(box: Box): number {
  let down = 0
  for (const run of box.runs) {
    const drop = DESCENDERS.test(run.text) ? DESCENT : 0
    down = Math.max(down, run.y + drop * run.size)
  }
  for (const rect of box.rects) down = Math.max(down, rect.y + rect.height)
  for (const stroke of box.strokes) {
    for (const [, y] of stroke.points)
      down = Math.max(down, y + stroke.width / 2)
  }
  return Math.max(0, down)
}

/**
 * Shift a box sideways and down, the two operations every rule below needs.
 *
 * Downwards because a box is laid out against its own baseline and then put
 * somewhere else in the formula: a numerator is "this box, a third of an em up".
 */
function shifted(box: Box, dx: number, dy: number): Box {
  return {
    ...box,
    runs: box.runs.map(run => ({...run, x: run.x + dx, y: run.y + dy})),
    rects: box.rects.map(rect => ({...rect, x: rect.x + dx, y: rect.y + dy})),
    strokes: box.strokes.map(stroke => ({
      ...stroke,
      points: stroke.points.map(
        ([x, y]) => [x + dx, y + dy] as [number, number],
      ),
    })),
  }
}

/** Put several boxes in a row, side by side, on one baseline. */
function rowOf(boxes: Box[]): Box {
  const box = emptyBox()
  let x = 0
  for (const item of boxes) {
    box.runs.push(...item.runs.map(run => ({...run, x: run.x + x})))
    box.rects.push(...item.rects.map(rect => ({...rect, x: rect.x + x})))
    box.strokes.push(
      ...item.strokes.map(stroke => ({
        ...stroke,
        points: stroke.points.map(
          ([px, py]) => [px + x, py] as [number, number],
        ),
      })),
    )
    box.width += item.width
    box.height = Math.max(box.height, item.height)
    box.depth = Math.max(box.depth, item.depth)
    x += item.width
  }
  return box
}

/**
 * Stack lines of a formula, centred on one another and clear of each other.
 *
 * The stack's baseline is the *first* line's baseline, so a formula written as
 * two lines sits on the page exactly where its first line would have.
 */
function stackLines(lines: Box[], size: number): Box {
  const width = Math.max(0, ...lines.map(line => line.width))
  const box = emptyBox(width)
  const gap = LINE_GAP * size
  let baseline = 0
  let previousDepth = 0
  lines.forEach((line, index) => {
    if (index > 0) baseline += previousDepth + gap + line.height
    const placed = shifted(line, (width - line.width) / 2, baseline)
    box.runs.push(...placed.runs)
    box.rects.push(...placed.rects)
    box.strokes.push(...placed.strokes)
    previousDepth = line.depth
  })
  box.height = lines[0]?.height ?? 0
  box.depth = baseline + previousDepth
  return box
}

/** Whether a symbol's limits belong above and below it rather than beside it. */
function takesLimits(node: TexNode): boolean {
  if (node.kind === 'sym') {
    return [...node.text].some(char => LIMIT_OPERATOR_GLYPHS.has(char))
  }
  if (node.kind === 'text') return LIMIT_OPERATOR_NAMES.has(node.text)
  return false
}

/** Lay out one node at a given style. */
function layoutNode(node: TexNode, style: Style, options: LayoutOptions): Box {
  const size = options.size * SIZE_OF[style]

  switch (node.kind) {
    case 'row': {
      const boxes: Box[] = []
      let line: Box[] = []
      /*
       * `\\` splits a formula into stacked lines — how a creator writes two
       * related statements, or a system of equations. Each line is laid out
       * independently and then centred, which is what makes the second line line
       * up under the middle of the first rather than under its first character.
       */
      let broken = false
      const flush = () => {
        if (!line.length) return
        boxes.push(rowOf(line))
        line = []
      }
      for (const item of node.items) {
        if (item.kind === 'space' && item.breakLine) {
          broken = true
          flush()
          continue
        }
        line.push(layoutNode(item, style, options))
      }
      flush()
      return broken ? stackLines(boxes, size) : rowOf(boxes)
    }

    case 'sym':
    case 'text':
    case 'literal': {
      /*
       * An unknown command is set in its own source spelling — visibly not
       * typeset, and still readable, which is what a creator needs in order to
       * fix it. `\text{...}` is the other half of the same idea: spaces are
       * printed there, and nowhere else in a formula.
       */
      const italic = node.kind === 'sym' ? node.italic : false
      const before = node.kind === 'sym' ? node.before * size : 0
      const after = node.kind === 'sym' ? node.after * size : 0
      const text = node.text
      const width = text ? options.measure(text, {italic, size}) : 0
      const box = emptyBox(before + width + after)
      box.height = ASCENT * size
      box.depth = DESCENT * size
      if (text) box.runs.push({text, italic, size, x: before, y: 0})
      return box
    }

    case 'space':
      return emptyBox(node.em * size)

    case 'frac': {
      const childStyle = inFraction[style]
      const num = layoutNode(node.num, childStyle, options)
      const den = layoutNode(node.den, childStyle, options)
      const rule = Math.max(1, RULE * size)
      const gap = FRACTION_GAP * size
      const pad = FRACTION_PAD * size
      const width = Math.max(num.width, den.width) + pad * 2
      const barCentre = rule / 2

      /*
       * Both children clear the bar by the same gap, and the gap is measured to
       * their *ink*: the numerator as far as anything in it hangs below its own
       * baseline, the denominator as far as anything in it rises above its own.
       *
       * Getting this wrong is what makes a hand-written fraction look wrong, and
       * it fails in both directions. Placing each child by its baseline alone —
       * the first version of this — left the denominator's ascent reaching up
       * *through* the bar and into the numerator's line, so the two lines and the
       * rule all collided. Placing each by its box instead reserved a descender
       * for every glyph: `x+1` has no tail, so its numerator floated while the
       * denominator stayed tight, and the fraction read as off-centre about its
       * own rule.
       *
       * The fraction's own extents still come from the finished placements, so
       * what it claims to occupy always covers what it draws.
       */
      const numY = -(barCentre + gap + inkBelow(num))
      const denY = barCentre + gap + inkAbove(den)

      const box = emptyBox(width)
      box.height = -numY + num.height
      box.depth = denY + den.depth
      box.rects.push({x: 0, y: -barCentre, width, height: rule})
      const placedNum = shifted(num, (width - num.width) / 2, numY)
      const placedDen = shifted(den, (width - den.width) / 2, denY)
      box.runs.push(...placedNum.runs, ...placedDen.runs)
      box.rects.push(...placedNum.rects, ...placedDen.rects)
      box.strokes.push(...placedNum.strokes, ...placedDen.strokes)
      return box
    }

    case 'sup':
    case 'sub':
    case 'supsub': {
      const base = layoutNode(node.base, style, options)

      /*
       * A big operator takes its bounds *stacked*, not at its shoulder — and
       * only while there is room, which in practice means the display style a
       * formula block is set in. A sigma with `i=1` beside it is a sigma in a
       * sentence; the same sigma with `i=1` above it is a sigma in a book.
       */
      if (style === 'display' && takesLimits(node.base)) {
        const upper =
          node.kind === 'supsub'
            ? node.sup
            : node.kind === 'sup'
              ? node.sup
              : undefined
        const lower =
          node.kind === 'supsub'
            ? node.sub
            : node.kind === 'sub'
              ? node.sub
              : undefined
        return limits(base, upper, lower, style, size, options)
      }

      const scriptStyle = inScript[style]
      const sup =
        'sup' in node ? layoutNode(node.sup, scriptStyle, options) : null
      const sub =
        'sub' in node ? layoutNode(node.sub, scriptStyle, options) : null
      const scriptSize = options.size * SIZE_OF[scriptStyle]

      const supShift = sup
        ? Math.max(SUP_SHIFT * size, base.height - 0.3 * scriptSize)
        : 0
      const subShift = sub
        ? Math.max(SUB_SHIFT * size, base.depth + 0.18 * scriptSize)
        : 0
      const scripts = Math.max(sup?.width ?? 0, sub?.width ?? 0)
      // A little air between a base and its scripts: two boxes touching read as
      // one glyph.
      const kiss = scripts > 0 ? 0.05 * size : 0

      const box = emptyBox(base.width + kiss + scripts)
      box.height = Math.max(base.height, sup ? supShift + sup.height : 0)
      box.depth = Math.max(base.depth, sub ? subShift + sub.depth : 0)
      box.runs.push(...base.runs)
      box.rects.push(...base.rects)
      box.strokes.push(...base.strokes)
      const scriptX = base.width + kiss
      for (const [script, dy] of [
        [sup, -supShift],
        [sub, subShift],
      ] as const) {
        if (!script) continue
        const placed = shifted(script, scriptX, dy)
        box.runs.push(...placed.runs)
        box.rects.push(...placed.rects)
        box.strokes.push(...placed.strokes)
      }
      return box
    }

    case 'sqrt': {
      // The radicand keeps its own style: TeX does not shrink what a root covers.
      const body = layoutNode(node.body, style, options)
      const index = node.index
        ? layoutNode(node.index, 'scriptscript', options)
        : null
      const rule = Math.max(1, RULE * size)
      const stroke = Math.max(1.2, RADICAL_RULE * size)
      const gap = RADICAL_GAP * size
      const radicalWidth = RADICAL_WIDTH * size
      const overhang = 0.06 * size

      /*
       * The bar sits over the radicand's *ink*, not over its box.
       *
       * Same argument as the fraction's clearance, one rule over: the box model
       * gives every glyph an ascender whether or not it has one, so a root over
       * a short letter — `\sqrt{x}`, which is most roots — was drawn with its bar
       * floating a fifth of an em above the letter, and the stroked radical
       * stretched to reach a height nothing in it wanted. Read to the ink, the
       * radical is exactly as tall as what it covers.
       *
       * The box's own extents below stay the generous envelope: this decides
       * where the rule is drawn, and stacking reads the box.
       */
      const bodyTop = -(inkAbove(body) + gap)
      const bottom = body.depth
      const reach = bottom - bodyTop
      const width = radicalWidth + body.width + overhang
      const box = emptyBox(width)
      box.height = body.height + gap + rule
      box.depth = body.depth

      // The bar sits just above what it covers, and reaches a little past it.
      box.rects.push({
        x: radicalWidth * 0.9,
        y: bodyTop - rule,
        width: width - radicalWidth * 0.9,
        height: rule,
      })
      /*
       * The stroke: a short lead, the valley, then the long climb to the bar. A
       * polyline rather than a glyph because no text face draws a radical the
       * height of an arbitrary box, and a root over a fraction is exactly the
       * case that needs one.
       */
      box.strokes.push({
        points: [
          [0, bodyTop + reach * 0.42],
          [radicalWidth * 0.18, bodyTop + reach * 0.42],
          [radicalWidth * 0.4, bodyTop + reach * 0.72],
          [radicalWidth * 0.9, bodyTop],
        ],
        width: stroke,
      })
      const placedBody = shifted(body, radicalWidth, 0)
      box.runs.push(...placedBody.runs)
      box.rects.push(...placedBody.rects)
      box.strokes.push(...placedBody.strokes)

      if (index) {
        /*
         * The index sits in the crook of the radical, above its valley — which is
         * what tells `\sqrt[3]{x}` apart from `\sqrt{3}x` at a glance.
         */
        const indexX = Math.max(0, radicalWidth * 0.34 - index.width)
        const indexY = bodyTop + reach * 0.3
        const placed = shifted(index, indexX, indexY)
        box.runs.push(...placed.runs)
        box.rects.push(...placed.rects)
        box.strokes.push(...placed.strokes)
        box.height = Math.max(box.height, -(indexY - index.height))
      }
      return box
    }

    case 'over': {
      const body = layoutNode(node.body, style, options)
      const rule = Math.max(1, RULE * size)
      const gap = 0.12 * size
      const box = emptyBox(body.width)
      box.depth = body.depth
      box.runs.push(...body.runs)
      box.rects.push(...body.rects)
      box.strokes.push(...body.strokes)
      // Over the ink, like the radical's bar above: an overline over `x` is a
      // rule over an x-height letter, not over the ascender line nothing in it
      // reaches.
      const top = -(inkAbove(body) + gap)
      if (node.decoration === 'bar') {
        box.height = body.height + gap + rule
        box.rects.push({x: 0, y: top - rule, width: body.width, height: rule})
      } else {
        /*
         * A vector: a rule with a head on its right end, drawn as two strokes.
         * The head is what makes it an arrow rather than a bar, and a physics
         * book is full of them.
         */
        const head = Math.max(2, 0.16 * size)
        box.height = body.height + gap + rule + head * 0.6
        const mid = top - rule / 2
        box.strokes.push({
          points: [
            [0, mid],
            [body.width, mid],
          ],
          width: rule,
        })
        box.strokes.push({
          points: [
            [body.width - head, mid - head * 0.5],
            [body.width, mid],
            [body.width - head, mid + head * 0.5],
          ],
          width: rule,
        })
      }
      return box
    }
  }
}

/** A big operator with its limits stacked over and under it. */
function limits(
  base: Box,
  upper: TexNode | undefined,
  lower: TexNode | undefined,
  style: Style,
  size: number,
  options: LayoutOptions,
): Box {
  const scriptStyle = inScript[style]
  const upperBox = upper ? layoutNode(upper, scriptStyle, options) : null
  const lowerBox = lower ? layoutNode(lower, scriptStyle, options) : null
  const scriptSize = options.size * SIZE_OF[scriptStyle]
  const gap = LIMITS_GAP * scriptSize

  /*
   * The operator itself is set larger than the text around it. A sigma printed at
   * the size of the sentence reads as a letter; a sigma with a sum under it has
   * to look like it is summarising something.
   */
  const scaledRuns = base.runs.map(run =>
    run.text.length === 1 && LIMIT_OPERATOR_GLYPHS.has(run.text)
      ? {...run, size: run.size * LIMIT_OPERATOR_SCALE}
      : run,
  )
  const baseHeight = base.height * LIMIT_OPERATOR_SCALE
  const baseDepth = base.depth * LIMIT_OPERATOR_SCALE

  const upperY = upperBox ? -(baseHeight + gap + upperBox.depth) : 0
  const lowerY = lowerBox ? baseDepth + gap + lowerBox.height : 0

  const box = emptyBox(
    Math.max(base.width, upperBox?.width ?? 0, lowerBox?.width ?? 0),
  )
  box.height = Math.max(
    baseHeight,
    upperBox ? Math.abs(upperY) + upperBox.height : 0,
  )
  box.depth = Math.max(baseDepth, lowerBox ? lowerY + lowerBox.depth : 0)

  /*
   * Everything is centred on one axis, not each on the box.
   *
   * They are not the same thing, and the difference is visible the moment a limit
   * is wider than its operator: `\sum_{i=1}^{n}` has a short sigma and a long
   * lower bound, and centring each in turn would leave the sigma sitting off to
   * one side of the sum it belongs to.
   */
  const axis = box.width / 2
  // The base is emitted run by run so its *sizes* can be raised, which means it is
  // placed by hand rather than through `shifted`.
  const baseX = axis - base.width / 2
  box.runs.push(...scaledRuns.map(run => ({...run, x: run.x + baseX})))
  box.rects.push(...base.rects.map(rect => ({...rect, x: rect.x + baseX})))
  box.strokes.push(
    ...base.strokes.map(stroke => ({
      ...stroke,
      points: stroke.points.map(([x, y]) => [x + baseX, y] as [number, number]),
    })),
  )

  for (const [part, dy] of [
    [upperBox, upperY],
    [lowerBox, lowerY],
  ] as const) {
    if (!part) continue
    const placed = shifted(part, axis - part.width / 2, dy)
    box.runs.push(...placed.runs)
    box.rects.push(...placed.rects)
    box.strokes.push(...placed.strokes)
  }
  return box
}

/**
 * Lay a formula out.
 *
 * The origin is the left edge of the formula on its baseline; `height` and
 * `depth` are how far the display list reaches above and below it. A caller
 * drawing the result into a box therefore offsets every y by `height` and gets an
 * image with no coordinates outside it.
 */
export function layoutFormula(
  node: TexNode,
  options: LayoutOptions,
): DisplayList {
  const box = layoutNode(node, 'display', options)
  return {
    runs: box.runs,
    rects: box.rects,
    strokes: box.strokes,
    width: box.width,
    height: box.height,
    depth: box.depth,
  }
}
